import {Link, Text, TextProps, useMultiStyleConfig, LinkProps} from '@chakra-ui/react'
import React from 'react'
import {useHeaderState} from '_context'
import {BannerHeader} from '_uiComponents'
import {usePageDesigner, useOpenModal} from '_customHooks'
import {BannerNewsItem, BannerNewsCta, Page, LinkObject} from '_types'
import {createRedirectUrl} from '_utils'
import {analyticsEventNewsletter} from '_analytics/events'

/**
 * @name Banner
 *
 * @component
 *
 * @description
 * Renders the banner header element under the header and retrieves all the information for it from Page deisgner
 *
 * @returns {React.FC} Rendered BannerHeader component.
 */
const Banner: React.FC = () => {
    const {isTransparentMode} = useHeaderState()
    const styles = useMultiStyleConfig('Banner') as Record<string, TextProps & LinkProps>
    const {openNewsletterModal} = useOpenModal()

    const {pageComponents, linkObject} = usePageDesigner({pageId: 'banner-news-section'})
    const slidersData = mapPageDesignerData(pageComponents)?.filter((slider) => slider.text)

    const createSlider = (text: string, linkObject: LinkObject, styleLink: LinkProps, index: number, links?: Array<BannerNewsCta>) => {
        if (links?.length === 0) return <>{text}</>

        const slicedString = text.split(/{[0-9]+}/) || []

        const triggerNewsletterModal = () => {
            analyticsEventNewsletter({action: 'start', label: 'header_stripbar'})

            openNewsletterModal({
                analyticsLabel: 'header_stripbar',
                analyticsEventNewsletter: () => analyticsEventNewsletter({action: 'dismiss', label: 'header_stripbar'}),
            })
        }

        return (
            <React.Fragment key={`banner-header-slider-inner-${index}`}>
                {slicedString.map((el, index) => {
                    const currentLink = links?.[index]
                    if (currentLink?.href) {
                        return (
                            <React.Fragment key={`banner-header-link-${index}`}>
                                {el}
                                {(index >= slicedString.length - 1 && text.indexOf(el) + el.length > text.length) ||
                                    (index < slicedString.length - 1 && (
                                        <Link
                                            {...styleLink}
                                            href={
                                                currentLink.href.includes('overlay-newsletter.html')
                                                    ? '#'
                                                    : createRedirectUrl(currentLink.href, linkObject.hostlink, linkObject.locale, linkObject.site)
                                            }
                                            onClick={currentLink.href.includes('overlay-newsletter.html') ? triggerNewsletterModal : () => {}}
                                            isExternal={currentLink.isExternal}
                                        >
                                            {currentLink.label_cta}
                                        </Link>
                                    ))}
                            </React.Fragment>
                        )
                    } else {
                        return <React.Fragment key={`banner-header-link-${index}`}>{el}</React.Fragment>
                    }
                })}
            </React.Fragment>
        )
    }

    return slidersData && slidersData.length > 0 ? (
        <BannerHeader variant={isTransparentMode ? 'transparent' : 'solid'}>
            {slidersData.map(
                (slider, index) =>
                    slider.text && (
                        <Text {...styles.text} key={`banner-header-slider-${index}`}>
                            {createSlider(slider.text, linkObject, {...styles.link}, index, slider.links)}
                        </Text>
                    ),
            )}
        </BannerHeader>
    ) : (
        <></>
    )
}

/**
 * Maps the page designer data.
 *
 * @param {Page} [pageComponents] - The components of the page.
 * @returns {Array<BannerNewsItem>} The slider of the banner news.
 */
const mapPageDesignerData = (pageComponents?: Page): Array<BannerNewsItem> => {
    const slidersData: Array<BannerNewsItem> = []

    const components = pageComponents?.regions?.[0]?.components?.[0]?.regions?.[0].components

    if (components) {
        components.forEach((component) => {
            slidersData.push({
                text: component?.data?.text,
                links:
                    component?.regions?.[0]?.components?.map((subComponent) => ({
                        id: subComponent.data?.analytics_id,
                        href: subComponent.data?.link_cta,
                        isExternal: subComponent.data?.target === '_blank',
                        label_cta: subComponent.data?.label_cta,
                    })) ?? [],
            })
        })
    }

    return slidersData
}

export {Banner}
